import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";
import { removePartyMovementBySource } from "@/lib/party-ledger";
import { fiscalYearLabel } from "@/features/purchase/constants";
import type { PurchaseOrderFormValues } from "@/features/purchase/schema";
import type {
  PurchaseOrder,
  PurchaseOrderLine,
  PurchaseOrderLineTax,
  PurchaseOrderListRow,
  PurchaseOrderStatus,
} from "@/features/purchase/types";
import { resolveApplicableTaxes } from "@/features/settings/constants";
import type { TaxRate } from "@/features/settings/types";

const text = (value: string | undefined) => value?.trim() || null;

function money(value: unknown) {
  const n = typeof value === "number" ? value : Number(value ?? 0);
  return Number.isNaN(n) ? 0 : n;
}

function hydratePo(raw: PurchaseOrder): PurchaseOrder {
  return {
    ...raw,
    subtotal: money(raw.subtotal),
    discount: money(raw.discount),
    tax_total: money(raw.tax_total),
    adjustment: money(raw.adjustment),
    grand_total: money(raw.grand_total),
  };
}

function hydrateLine(raw: PurchaseOrderLine): PurchaseOrderLine {
  return {
    ...raw,
    qty: money(raw.qty),
    weight: money(raw.weight),
    unit_rate: money(raw.unit_rate),
    line_subtotal: money(raw.line_subtotal),
    gst_slab: money(raw.gst_slab),
    ordered_weight: money(raw.ordered_weight),
    received_weight: money(raw.received_weight),
    thickness: raw.thickness == null ? null : money(raw.thickness),
    width: raw.width == null ? null : money(raw.width),
    length: raw.length == null ? null : money(raw.length),
    od: raw.od == null ? null : money(raw.od),
    inbound_process_type_id: (raw.inbound_process_type_id as string | null) ?? null,
    taxes: (raw.taxes ?? []).map((t) => ({
      ...t,
      rate: money(t.rate),
      amount: money(t.amount),
    })),
  };
}

export type ComputedLineTax = {
  tax_rate_id: string | null;
  tax_type: string;
  rate: number;
  amount: number;
};

export type ComputedLine = {
  line_subtotal: number;
  ordered_weight: number;
  taxes: ComputedLineTax[];
  tax_total: number;
};

export function computeLineAmounts(
  line: { qty: number; weight: number; unit_rate: number; gst_slab: number },
  rates: TaxRate[],
  companyState: string | null | undefined,
  vendorPlaceOfSupply: string | null | undefined,
  asOf: string,
): ComputedLine {
  // Rate is always weight-based when weight is present (tube/CTL auto-fill weight).
  const basis = line.weight > 0 ? line.weight : line.qty;
  const line_subtotal = Math.round(basis * line.unit_rate * 100) / 100;
  const { taxes } = resolveApplicableTaxes({
    rates,
    gstSlab: line.gst_slab,
    companyState,
    clientPlaceOfSupply: vendorPlaceOfSupply,
    asOf,
  });
  const computedTaxes: ComputedLineTax[] = taxes.map((t) => ({
    tax_rate_id: t.id,
    tax_type: t.tax_type,
    rate: t.rate,
    amount: Math.round(line_subtotal * (t.rate / 100) * 100) / 100,
  }));
  const tax_total = computedTaxes.reduce((s, t) => s + t.amount, 0);
  return {
    line_subtotal,
    ordered_weight: line.weight > 0 ? line.weight : line.qty,
    taxes: computedTaxes,
    tax_total,
  };
}

export function computePoTotals(
  lines: ComputedLine[],
  discount: number,
  adjustment: number,
) {
  const subtotal = lines.reduce((s, l) => s + l.line_subtotal, 0);
  const tax_total = lines.reduce((s, l) => s + l.tax_total, 0);
  const grand_total =
    Math.round((subtotal - discount + tax_total + adjustment) * 100) / 100;
  return {
    subtotal: Math.round(subtotal * 100) / 100,
    tax_total: Math.round(tax_total * 100) / 100,
    discount,
    adjustment,
    grand_total,
  };
}

async function nextPoNumber(issueDate: string): Promise<string> {
  const { data: company } = await supabase
    .from("company")
    .select("po_prefix, fiscal_year_start_month")
    .limit(1)
    .maybeSingle();
  const prefix = ((company?.po_prefix as string | undefined) ?? "PO").trim().toUpperCase() || "PO";
  const fy = fiscalYearLabel(issueDate, Number(company?.fiscal_year_start_month) || 4);
  const pattern = `${prefix}-${fy}-`;
  const { data, error } = await supabase
    .from("purchase_orders")
    .select("po_number")
    .ilike("po_number", `${pattern}%`)
    .order("po_number", { ascending: false })
    .limit(1);
  if (error) throw error;
  const last = data?.[0]?.po_number as string | undefined;
  const n = last ? Number.parseInt(last.replace(pattern, ""), 10) : 0;
  const next = (Number.isNaN(n) ? 0 : n) + 1;
  return `${pattern}${String(next).padStart(4, "0")}`;
}

async function loadVendorPos(vendorId: string) {
  const { data, error } = await supabase
    .from("vendors")
    .select("place_of_supply, state, payment_terms")
    .eq("id", vendorId)
    .single();
  if (error) throw error;
  return data as { place_of_supply: string | null; state: string; payment_terms: string };
}

async function loadCompanyState() {
  const { data } = await supabase.from("company").select("state, name, addr1, city, pincode").limit(1).maybeSingle();
  return data as {
    state: string | null;
    name: string | null;
    addr1: string | null;
    city: string | null;
    pincode: string | null;
  } | null;
}

async function loadTaxRates(): Promise<TaxRate[]> {
  const { data, error } = await supabase.from("tax_rates").select("*").eq("is_active", true);
  if (error) throw error;
  return (data ?? []).map((r) => ({
    ...(r as TaxRate),
    rate: money((r as TaxRate).rate),
    gst_slab:
      (r as TaxRate).gst_slab === null || (r as TaxRate).gst_slab === undefined
        ? null
        : money((r as TaxRate).gst_slab),
  }));
}

async function replaceLines(
  poId: string,
  values: PurchaseOrderFormValues,
  rates: TaxRate[],
  companyState: string | null | undefined,
  vendorPos: string | null | undefined,
) {
  const { error: delErr } = await supabase
    .from("purchase_order_lines")
    .delete()
    .eq("purchase_order_id", poId);
  if (delErr) throw delErr;

  const computedLines: ComputedLine[] = [];
  for (let i = 0; i < values.lines.length; i++) {
    const line = values.lines[i];
    const computed = computeLineAmounts(
      line,
      rates,
      companyState,
      vendorPos,
      values.issue_date,
    );
    computedLines.push(computed);

    const { data: inserted, error: lineErr } = await supabase
      .from("purchase_order_lines")
      .insert({
        purchase_order_id: poId,
        line_no: i + 1,
        material_id: line.material_id,
        material_grade_id: text(line.material_grade_id),
        shape_size_id: text(line.shape_size_id),
        description: text(line.description),
        qty: line.qty,
        weight: line.weight,
        uom_id: text(line.uom_id),
        unit_rate: line.unit_rate,
        line_subtotal: computed.line_subtotal,
        gst_slab: line.gst_slab,
        ordered_weight: computed.ordered_weight,
        received_weight: 0,
        is_fully_received: false,
        thickness: line.thickness ?? null,
        width: line.width ?? null,
        length: line.length ?? null,
        od: line.od ?? null,
        inbound_process_type_id: text(line.inbound_process_type_id),
      })
      .select("id")
      .single();
    if (lineErr) throw lineErr;

    if (computed.taxes.length) {
      const { error: taxErr } = await supabase.from("purchase_order_line_taxes").insert(
        computed.taxes.map((t) => ({
          line_id: inserted.id,
          tax_rate_id: t.tax_rate_id,
          tax_type: t.tax_type,
          rate: t.rate,
          amount: t.amount,
        })),
      );
      if (taxErr) throw taxErr;
    }
  }

  return computePoTotals(computedLines, values.discount, values.adjustment);
}

/**
 * Vendor AP for POs is posted per goods receipt (received weight × rate + GST),
 * keyed by source goods_receipt. On cancel, remove any legacy PO-level bill.
 */
async function syncApBill(po: PurchaseOrder) {
  if (po.status === "cancelled") {
    await removePartyMovementBySource({
      party: "vendor",
      partyId: po.vendor_id,
      sourceType: "purchase_order",
      sourceId: po.id,
    });
  }
}

export function usePurchaseOrders(filters?: { vendorId?: string }) {
  return useQuery({
    queryKey: ["purchase_orders", filters?.vendorId ?? "all"],
    enabled: isSupabaseConfigured,
    queryFn: async (): Promise<PurchaseOrderListRow[]> => {
      let q = supabase
        .from("purchase_orders")
        .select("*, vendors(code, name), purchase_order_lines(id)")
        .order("issue_date", { ascending: false })
        .order("created_at", { ascending: false });
      if (filters?.vendorId) q = q.eq("vendor_id", filters.vendorId);
      const { data, error } = await q;
      if (error) throw error;

      return (data ?? []).map((row) => {
        const raw = row as Record<string, unknown>;
        const vendor = raw.vendors as { code?: string; name?: string } | null;
        const lines = raw.purchase_order_lines as { id: string }[] | null;
        const { vendors: _v, purchase_order_lines: _l, ...rest } = raw;
        return {
          ...hydratePo(rest as unknown as PurchaseOrder),
          vendor_code: vendor?.code ?? null,
          vendor_name: vendor?.name ?? null,
          line_count: lines?.length ?? 0,
        };
      });
    },
  });
}

export function usePurchaseOrder(id?: string) {
  return useQuery({
    queryKey: ["purchase_orders", id],
    enabled: isSupabaseConfigured && Boolean(id),
    queryFn: async (): Promise<PurchaseOrder> => {
      const { data, error } = await supabase
        .from("purchase_orders")
        .select(
          `*,
          vendors(code, name, place_of_supply, state),
          purchase_order_lines(
            *,
            purchase_order_line_taxes(*),
            materials(name, requires_shape_size, parent_material_id),
            material_grades(name),
            shape_sizes(size_label),
            uoms(code)
          )`,
        )
        .eq("id", id!)
        .single();
      if (error) throw error;

      const raw = data as Record<string, unknown>;
      const vendor = raw.vendors as {
        code?: string;
        name?: string;
      } | null;
      const linesRaw = (raw.purchase_order_lines as Record<string, unknown>[] | null) ?? [];
      const { vendors: _v, purchase_order_lines: _l, ...rest } = raw;

      const lines = linesRaw
        .map((line) => {
          const taxes = (
            (line.purchase_order_line_taxes as PurchaseOrderLineTax[] | null) ?? []
          ).map((t) => ({ ...t, rate: money(t.rate), amount: money(t.amount) }));
          const material = line.materials as {
            name?: string;
            requires_shape_size?: boolean;
            parent_material_id?: string | null;
          } | null;
          const grade = line.material_grades as { name?: string } | null;
          const shape = line.shape_sizes as { size_label?: string } | null;
          const uom = line.uoms as { code?: string } | null;
          const {
            purchase_order_line_taxes: _t,
            materials: _m,
            material_grades: _g,
            shape_sizes: _s,
            uoms: _u,
            ...lineRest
          } = line;
          return hydrateLine({
            ...(lineRest as unknown as PurchaseOrderLine),
            taxes,
            material_name: material?.name ?? null,
            grade_name: grade?.name ?? null,
            shape_label: shape?.size_label ?? null,
            uom_code: uom?.code ?? null,
            requires_shape_size: Boolean(material?.requires_shape_size),
            parent_material_id: material?.parent_material_id ?? null,
          });
        })
        .sort((a, b) => a.line_no - b.line_no);

      return {
        ...hydratePo(rest as unknown as PurchaseOrder),
        vendor_code: vendor?.code ?? null,
        vendor_name: vendor?.name ?? null,
        lines,
      };
    },
  });
}

export function useCreatePurchaseOrder() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      values,
      send = false,
    }: {
      values: PurchaseOrderFormValues;
      send?: boolean;
    }) => {
      const [vendor, company, rates] = await Promise.all([
        loadVendorPos(values.vendor_id),
        loadCompanyState(),
        loadTaxRates(),
      ]);
      const poNumber = await nextPoNumber(values.issue_date);
      const billTo = company
        ? [company.name, company.addr1, [company.city, company.pincode].filter(Boolean).join(" — ")]
            .filter(Boolean)
            .join("\n")
        : text(values.bill_to_text);

      const { data: header, error } = await supabase
        .from("purchase_orders")
        .insert({
          po_number: poNumber,
          vendor_id: values.vendor_id,
          issue_date: values.issue_date,
          due_date: text(values.due_date),
          status: send ? "sent" : "draft",
          bill_to_text: text(values.bill_to_text) ?? billTo,
          ship_to_text: text(values.ship_to_text),
          payment_terms: text(values.payment_terms) ?? vendor.payment_terms,
          notes: text(values.notes),
          terms: text(values.terms),
          discount: values.discount,
          adjustment: values.adjustment,
          created_by: user?.id ?? null,
          updated_by: user?.id ?? null,
        })
        .select("*")
        .single();
      if (error) throw error;

      const vendorPos = vendor.place_of_supply || vendor.state;
      const totals = await replaceLines(
        header.id,
        values,
        rates,
        company?.state,
        vendorPos,
      );

      const { data: updated, error: updErr } = await supabase
        .from("purchase_orders")
        .update({ ...totals, updated_at: new Date().toISOString() })
        .eq("id", header.id)
        .select("*")
        .single();
      if (updErr) throw updErr;
      return hydratePo(updated as PurchaseOrder);
    },
    onSuccess: async (_d, vars) => {
      await queryClient.invalidateQueries({ queryKey: ["purchase_orders"] });
      toast.success(vars.send ? "Purchase order created and sent" : "Purchase order created");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to create PO"),
  });
}

export function useUpdatePurchaseOrder() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      id,
      values,
      replaceLines: doReplace,
      send = false,
    }: {
      id: string;
      values: PurchaseOrderFormValues;
      replaceLines: boolean;
      send?: boolean;
    }) => {
      const { data: existing, error: exErr } = await supabase
        .from("purchase_orders")
        .select("*")
        .eq("id", id)
        .single();
      if (exErr) throw exErr;
      const po = hydratePo(existing as PurchaseOrder);

      if (po.status === "cancelled" || po.status === "closed") {
        throw new Error("Cannot edit a closed or cancelled PO");
      }

      let totals = {
        subtotal: po.subtotal,
        discount: values.discount,
        tax_total: po.tax_total,
        adjustment: values.adjustment,
        grand_total: po.grand_total,
      };

      if (doReplace && po.status === "draft") {
        const [vendor, company, rates] = await Promise.all([
          loadVendorPos(values.vendor_id),
          loadCompanyState(),
          loadTaxRates(),
        ]);
        totals = await replaceLines(
          id,
          values,
          rates,
          company?.state,
          vendor.place_of_supply || vendor.state,
        );
      } else {
        // Recalc grand from existing tax/subtotal if only header money fields change
        totals = {
          subtotal: po.subtotal,
          discount: values.discount,
          tax_total: po.tax_total,
          adjustment: values.adjustment,
          grand_total:
            Math.round((po.subtotal - values.discount + po.tax_total + values.adjustment) * 100) /
            100,
        };
      }

      const nextStatus = send && po.status === "draft" ? "sent" : po.status;

      const { data, error } = await supabase
        .from("purchase_orders")
        .update({
          vendor_id: po.status === "draft" ? values.vendor_id : po.vendor_id,
          issue_date: values.issue_date,
          due_date: text(values.due_date),
          payment_terms: text(values.payment_terms),
          bill_to_text: text(values.bill_to_text),
          ship_to_text: text(values.ship_to_text),
          notes: text(values.notes),
          terms: text(values.terms),
          status: nextStatus,
          ...totals,
          updated_by: user?.id ?? null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id)
        .select("*")
        .single();
      if (error) throw error;
      return hydratePo(data as PurchaseOrder);
    },
    onSuccess: async (_d, vars) => {
      await queryClient.invalidateQueries({ queryKey: ["purchase_orders"] });
      await queryClient.invalidateQueries({ queryKey: ["purchase_orders", vars.id] });
      toast.success(vars.send ? "Purchase order saved and sent" : "Purchase order updated");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to update PO"),
  });
}

export function useUpdatePurchaseOrderStatus() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: PurchaseOrderStatus }) => {
      const { data: existing, error: exErr } = await supabase
        .from("purchase_orders")
        .select("*")
        .eq("id", id)
        .single();
      if (exErr) throw exErr;
      const po = hydratePo(existing as PurchaseOrder);

      const { data, error } = await supabase
        .from("purchase_orders")
        .update({
          status,
          updated_by: user?.id ?? null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id)
        .select("*")
        .single();
      if (error) throw error;
      const updated = hydratePo(data as PurchaseOrder);

      const prev = po.status;
      const next = updated.status;
      if (next === "cancelled" && prev !== "cancelled") {
        await syncApBill(updated);
      }

      return updated;
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["purchase_orders"] });
      await queryClient.invalidateQueries({ queryKey: ["purchase_orders", data.id] });
      await queryClient.invalidateQueries({ queryKey: ["vendor_ap_movements"] });
      await queryClient.invalidateQueries({ queryKey: ["master_ledger"] });
      await queryClient.invalidateQueries({ queryKey: ["vendors"] });
      toast.success(`Status → ${data.status}`);
    },
    onError: (error: Error) => toast.error(error.message || "Failed to update status"),
  });
}

export function useDeletePurchaseOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data: existing, error: exErr } = await supabase
        .from("purchase_orders")
        .select("*")
        .eq("id", id)
        .single();
      if (exErr) throw exErr;
      const po = hydratePo(existing as PurchaseOrder);
      if (po.status !== "draft") throw new Error("Only draft POs can be deleted");

      const { error } = await supabase.from("purchase_orders").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["purchase_orders"] });
      toast.success("Purchase order deleted");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to delete PO"),
  });
}

export function purchaseOrderToForm(po: PurchaseOrder): PurchaseOrderFormValues {
  return {
    vendor_id: po.vendor_id,
    issue_date: po.issue_date,
    due_date: po.due_date ?? "",
    payment_terms: po.payment_terms ?? "",
    bill_to_text: po.bill_to_text ?? "",
    ship_to_text: po.ship_to_text ?? "",
    discount: po.discount,
    adjustment: po.adjustment,
    notes: po.notes ?? "",
    terms: po.terms ?? "",
    lines: (po.lines ?? []).map((l) => ({
      id: l.id,
      material_id: l.material_id,
      material_grade_id: l.material_grade_id ?? "",
      shape_size_id: l.shape_size_id ?? "",
      description: l.description ?? "",
      qty: l.qty,
      weight: l.weight,
      uom_id: l.uom_id ?? "",
      unit_rate: l.unit_rate,
      gst_slab: l.gst_slab,
      thickness: l.thickness ?? null,
      width: l.width ?? null,
      length: l.length ?? null,
      od: l.od ?? null,
      inbound_process_type_id: l.inbound_process_type_id ?? "",
    })),
  };
}
